import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, Subject, Subscription, catchError, concatMap, filter, forkJoin, map, of, switchMap, take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { PontoDeVendaService } from '../../../../services/ponto-de-venda.service';
import { ParametrosService } from '../../../../services/parametros.service';
import { CadastroService } from '../../../../services/cadastro.service';
import { ModalStateService } from '../../../../services/modaState.service';
import { ErrorModel } from '../../../../models/error.model';
import {
  PDV_ERROS,
  PDV_ERROS_LEITURA,
  PdvAtualizarItensRequest,
  PdvFinalizarResponse,
  PdvVenda,
  ProdutoPdv,
} from '../../../../models/ponto-de-venda.model';
import {
  ItemCarrinho,
  ProdutoCarrinho,
  adicionarOuSomar,
  alterarQuantidade,
  calcularTotais,
  descontoValido,
  normalizarLeitura,
  parseQuantidade,
  permiteFracionadoPorUnidade,
  removerItem,
  totalLinhaCentavos,
} from '../../../../core/utils/ponto-de-venda.util';
import { todayLocalDateString } from '../../../../core/utils/date.util';
import { formatarMoedaBRL, formatarNumeroPtBR } from '../../../../core/utils/moeda.util';
import { ConfirmDialogComponent } from '../../../modal/confirm-dialog/confirm-dialog';
import { VendaModalV2 } from '../../../modal/venda-modal-v2/venda-modal-v2';
import { ClienteModal } from '../../../modal/cliente-modal/cliente-modal';
import {
  CUPOM_LABELS_FORMA,
  CUPOM_LABELS_TIPO_VENDA,
  CupomVendaModal,
} from '../../../modal/cupom-venda-modal/cupom-venda-modal';
import { CupomVendaBalcao } from '../../../modal/venda-balcao-cupom-modal/venda-balcao-cupom-modal';
import { OrcamentoDTO } from '../../../../models/financeiro.model';
import { ClienteSimplificado, Empresa, VendedorCadastro } from '../../../../models/cadastro.model';
import { ResumoVendaV2 } from '../../../../models/venda-v2.model';

/** Limite inicial de desconto (%) até o parâmetro PDV_DESCONTO_LIMITE responder (plano P6). */
export const PDV_DESCONTO_LIMITE_PADRAO = 3;
const PDV_DESCONTO_CATEGORIA = 'PDV_DESCONTO_LIMITE';
const TEMPO_DESFAZER_MS = 5000;
export const PDV_CHAVE_STORAGE = 'pdv.chaveIdempotencia';
/** Backoff do reenvio automático (plano P3): 1s, 2s, 5s; depois só pelo botão. */
export const PDV_BACKOFF_MS: readonly number[] = [1000, 2000, 5000];

export type StatusSync = 'sincronizado' | 'sincronizando' | 'erro';

type ResultadoLeitura =
  | { ok: true; produto: ProdutoPdv }
  | { ok: false; mensagem: string };

interface LeituraPendente {
  codigo: string;
}

export interface LinhaPdv {
  item: ItemCarrinho;
  totalFormatado: string;
  precoFormatado: string;
  quantidadeFormatada: string;
}

@Component({
  selector: 'app-ponto-de-venda',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ConfirmDialogComponent, VendaModalV2, ClienteModal, CupomVendaModal],
  templateUrl: './ponto-de-venda.component.html',
  styleUrls: ['./ponto-de-venda.component.css'],
})
export class PontoDeVendaComponent implements OnInit, OnDestroy {
  private readonly pdvService = inject(PontoDeVendaService);
  private readonly parametrosService = inject(ParametrosService);
  private readonly toastr = inject(ToastrService);
  private readonly cadastroService = inject(CadastroService);
  private readonly modalStateService = inject(ModalStateService);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('leituraInput') leituraInput?: ElementRef<HTMLInputElement>;
  @ViewChild('qtdInput') qtdInput?: ElementRef<HTMLInputElement>;

  readonly codigoControl = new FormControl<string>('', { nonNullable: true });
  readonly descontoControl = new FormControl<number | null>(null, [Validators.min(0)]);
  readonly quantidadeControl = new FormControl<string>('', { nonNullable: true });

  itens: ItemCarrinho[] = [];
  linhas: LinhaPdv[] = [];
  selecionadoId: number | null = null;
  ultimoLidoId: number | null = null;
  editandoId: number | null = null;
  erroQuantidade: string | null = null;
  erroLeitura: string | null = null;
  descontoMaximo = PDV_DESCONTO_LIMITE_PADRAO;
  confirmarCancelamentoAberto = false;
  confirmarSaidaAberto = false;
  statusSync: StatusSync = 'sincronizado';
  rascunhoPendente: PdvVenda | null = null;
  precosAlterados = new Set<number>();

  // Fechamento (P9) e identificação de cliente (P7).
  clienteModalAberto = false;
  clienteNome: string | null = null;
  vendaModalAberto = false;
  orcamentoFechamento: OrcamentoDTO | null = null;
  restricaoCliente = false;
  cupomAberto = false;
  cupom: CupomVendaBalcao | null = null;
  finalizando = false;
  reabrindo = false;
  concluindo = false;

  subtotalFormatado = formatarMoedaBRL(0);
  descontoValorFormatado = formatarMoedaBRL(0);
  totalFormatado = formatarMoedaBRL(0);
  totalCentavos = 0;
  finalizarHabilitado = false;
  readonly cancelMessage = 'Descartar todos os itens desta venda?';
  readonly saidaMessage = 'Sair da tela descarta a venda em andamento. Deseja sair?';
  readonly saidaMessageNaoSincronizada = 'Há itens ainda não sincronizados com o servidor. Sair descarta a venda em andamento. Deseja sair?';

  /** Cache em memória por sessão (o servidor prevalece na reconciliação do S5). */
  private readonly cache = new Map<string, ProdutoPdv>();
  private readonly leituras$ = new Subject<LeituraPendente>();
  private readonly subs = new Subscription();
  private removidos: { item: ItemCarrinho; indice: number }[] = [];

  // Sincronização do orçamento invisível (P3).
  private chave = '';
  private vendaId: number | null = null;
  private sessao = 0;
  private emVoo = false;
  private sujo = false;
  private tentativa = 0;
  private timerRetry: ReturnType<typeof setTimeout> | null = null;
  private ultimoDescontoAceito: number | null = null;
  private saidaResposta$: Subject<boolean> | null = null;
  private idCliente: number | null = null;
  private clienteAnonimo = true;
  private clienteDocumento = '';
  private resumoVenda: ResumoVendaV2 | null = null;
  private identificacaoSub: Subscription | null = null;
  private vendaConcluida = false;
  private leiturasPendentes = 0;

  ngOnInit(): void {
    this.chave = this.obterChave();
    this.carregarRascunhos();
    this.subs.add(
      this.leituras$
        .pipe(concatMap((leitura) => this.resolverProduto(leitura.codigo)))
        .subscribe((resultado) => {
          this.leiturasPendentes = Math.max(0, this.leiturasPendentes - 1);
          this.aplicarLeitura(resultado);
        }),
    );
    this.subs.add(
      this.descontoControl.valueChanges.subscribe(() => {
        this.recalcular();
        if (!this.descontoInvalido) this.solicitarSync();
      }),
    );
    this.carregarLimiteDesconto();
    this.recalcular();
    this.focarLeitura();
  }

  ngOnDestroy(): void {
    this.limparRetry();
    this.identificacaoSub?.unsubscribe();
    this.subs.unsubscribe();
  }

  // ---------- Sincronização (orçamento invisível) ----------

  private obterChave(): string {
    let chave = sessionStorage.getItem(PDV_CHAVE_STORAGE);
    if (!chave) {
      chave = crypto.randomUUID();
      sessionStorage.setItem(PDV_CHAVE_STORAGE, chave);
    }
    return chave;
  }

  private zerarChave(): void {
    sessionStorage.removeItem(PDV_CHAVE_STORAGE);
    this.chave = this.obterChave();
  }

  private montarPayload(): PdvAtualizarItensRequest {
    return {
      itens: this.itens.map((i) => ({ produtoId: i.produtoId, quantidade: i.quantidade })),
      descontoPercentual: this.descontoInvalido ? this.ultimoDescontoAceito : this.descontoControl.value,
    };
  }

  /** Marca o estado como sujo e dispara o envio se não houver um em voo (coalescência). */
  private solicitarSync(): void {
    if (this.rascunhoPendente) return;
    if (this.vendaId === null && this.itens.length === 0) return;
    this.sujo = true;
    if (this.emVoo) return;
    this.executarSync();
  }

  /** Botão "Tentar novamente". */
  tentarNovamente(): void {
    if (this.emVoo) return;
    this.limparRetry();
    this.tentativa = 0;
    this.sujo = true;
    this.executarSync();
  }

  private executarSync(): void {
    this.limparRetry();
    const sessao = this.sessao;
    const payload = this.montarPayload();
    this.emVoo = true;
    this.sujo = false;
    this.definirStatus('sincronizando');

    const requisicao$ =
      this.vendaId === null
        ? this.criarEAtualizar(sessao, payload)
        : this.pdvService.atualizarItens(this.vendaId, payload);

    requisicao$.subscribe({
      next: (venda) => {
        if (sessao !== this.sessao) return;
        this.emVoo = false;
        this.tentativa = 0;
        this.ultimoDescontoAceito = payload.descontoPercentual;
        if (this.sujo) {
          this.executarSync();
          return;
        }
        this.definirStatus('sincronizado');
        this.reconciliar(venda);
      },
      error: (erro: unknown) => {
        if (sessao !== this.sessao) return;
        this.emVoo = false;
        this.tratarErroSync(erro, payload);
      },
    });
  }

  /** Cria o rascunho; se o cliente mudou durante o criar, descarta-o e recria com o cliente atual. */
  private criarEAtualizar(sessao: number, payload: PdvAtualizarItensRequest): Observable<PdvVenda> {
    const idClienteEnviado = this.idCliente;
    return this.pdvService
      .criar({ chaveIdempotencia: this.chave, ...(idClienteEnviado !== null ? { idCliente: idClienteEnviado } : {}) })
      .pipe(
        switchMap((venda) => {
          if (sessao !== this.sessao) {
            // Venda cancelada enquanto o criar estava em voo: descarta o rascunho órfão.
            this.pdvService.cancelar(venda.id).subscribe({ error: () => undefined });
            return EMPTY;
          }
          if (this.idCliente !== idClienteEnviado) {
            this.pdvService.cancelar(venda.id).subscribe({ error: () => undefined });
            this.zerarChave();
            return this.criarEAtualizar(sessao, payload);
          }
          this.vendaId = venda.id;
          return this.pdvService.atualizarItens(venda.id, payload);
        }),
      );
  }

  private tratarErroSync(erro: unknown, enviado: PdvAtualizarItensRequest): void {
    const corpo = erro instanceof HttpErrorResponse ? (erro.error as Partial<ErrorModel> | null) : null;
    const status = erro instanceof HttpErrorResponse ? erro.status : 0;

    if (corpo?.code === PDV_ERROS.PDV_DESCONTO_ACIMA_LIMITE) {
      this.toastr.error(corpo.message ?? 'Desconto acima do limite permitido');
      const revertido = this.ultimoDescontoAceito === enviado.descontoPercentual ? null : this.ultimoDescontoAceito;
      this.ultimoDescontoAceito = revertido;
      this.definirStatus('sincronizando');
      this.descontoControl.setValue(revertido);
      this.solicitarSync();
      if (!this.emVoo && this.statusSync === 'sincronizando') this.definirStatus('sincronizado');
      return;
    }

    this.definirStatus('erro');
    const transitorio = status === 0 || status >= 500;
    if (!transitorio) {
      this.toastr.error(corpo?.message ?? 'Não foi possível sincronizar a venda');
      return;
    }
    if (this.tentativa < PDV_BACKOFF_MS.length) {
      const atraso = PDV_BACKOFF_MS[this.tentativa];
      this.tentativa++;
      this.timerRetry = setTimeout(() => {
        this.timerRetry = null;
        if (!this.emVoo) this.executarSync();
      }, atraso);
    }
  }

  private limparRetry(): void {
    if (this.timerRetry !== null) {
      clearTimeout(this.timerRetry);
      this.timerRetry = null;
    }
  }

  private definirStatus(status: StatusSync): void {
    this.statusSync = status;
    this.finalizarHabilitado = this.calcularFinalizarHabilitado();
    this.cdr.detectChanges();
  }

  /** O servidor prevalece: preço, limite e total devolvidos substituem os locais. */
  private reconciliar(venda: PdvVenda): void {
    if (typeof venda.clienteAnonimo === 'boolean') {
      this.clienteAnonimo = venda.clienteAnonimo;
      if (venda.clienteAnonimo) {
        this.clienteNome = null;
        this.clienteDocumento = '';
      } else if (venda.nomeCliente) {
        this.clienteNome = venda.nomeCliente;
      }
    }
    const alterados = new Set<number>();
    this.itens = this.itens.map((item) => {
      const doServidor = venda.itens?.find((i) => i.produtoId === item.produtoId);
      if (doServidor && doServidor.precoUnitario !== item.precoUnitario) {
        alterados.add(item.produtoId);
        return { ...item, precoUnitario: doServidor.precoUnitario };
      }
      return item;
    });
    this.precosAlterados = alterados;
    if (alterados.size > 0) {
      this.toastr.info('Preço atualizado pelo servidor em um ou mais itens.');
    }
    if (typeof venda.descontoMaximoPercentual === 'number' && Number.isFinite(venda.descontoMaximoPercentual)) {
      this.descontoMaximo = venda.descontoMaximoPercentual;
    }
    this.recalcular();
    if (typeof venda.valorTotal === 'number' && Math.round(venda.valorTotal * 100) !== this.totalCentavos) {
      this.totalCentavos = Math.round(venda.valorTotal * 100);
      this.totalFormatado = formatarMoedaBRL(venda.valorTotal);
      this.subtotalFormatado = formatarMoedaBRL(venda.subtotal);
      this.descontoValorFormatado = formatarMoedaBRL(venda.desconto);
      this.cdr.detectChanges();
    }
  }

  // ---------- Retomada / abandono ----------

  private carregarRascunhos(): void {
    this.pdvService.emAndamento().subscribe({
      next: (rascunhos) => {
        // A sessão já tem venda (ou criação em voo/itens lidos): o servidor não pode oferecer retomada.
        const sessaoAtiva = this.vendaId !== null || this.emVoo || this.itens.length > 0;
        const outros = (rascunhos ?? []).filter((r) => r.id !== this.vendaId);
        this.rascunhoPendente = !sessaoAtiva && outros.length ? outros[0] : null;
        this.finalizarHabilitado = this.calcularFinalizarHabilitado();
        this.cdr.detectChanges();
      },
      error: () => {
        // Sem rascunhos conhecidos: segue como venda nova.
      },
    });
  }

  retomar(): void {
    const venda = this.rascunhoPendente;
    if (!venda) return;
    this.rascunhoPendente = null;
    // Qualquer rascunho da sessão corrente (ou criação em voo) é descartado para não ficar órfão.
    const anterior = this.zerarSessao();
    if (anterior !== null && anterior !== venda.id) {
      this.pdvService.cancelar(anterior).subscribe({ error: () => undefined });
    }
    this.vendaId = venda.id;
    this.itens = (venda.itens ?? []).map((i) => ({
      produtoId: i.produtoId,
      codigoBarra: '',
      descricao: i.nome,
      unMedida: i.unMedida,
      permiteFracionado: permiteFracionadoPorUnidade(i.unMedida),
      precoUnitario: i.precoUnitario,
      quantidade: i.quantidade,
    }));
    this.ultimoDescontoAceito = venda.descontoPercentual;
    this.clienteAnonimo = venda.clienteAnonimo !== false;
    this.clienteNome = this.clienteAnonimo ? null : (venda.nomeCliente ?? null);
    if (typeof venda.descontoMaximoPercentual === 'number' && Number.isFinite(venda.descontoMaximoPercentual)) {
      this.descontoMaximo = venda.descontoMaximoPercentual;
    }
    this.descontoControl.setValue(venda.descontoPercentual, { emitEvent: false });
    this.selecionadoId = null;
    this.ultimoLidoId = null;
    this.recalcular();
    this.focarLeitura();
  }

  descartarRascunho(): void {
    const venda = this.rascunhoPendente;
    if (!venda) return;
    this.rascunhoPendente = null;
    this.zerarChave();
    this.cdr.detectChanges();
    this.pdvService.cancelar(venda.id).subscribe({
      next: () => this.carregarRascunhos(),
      error: (erro: unknown) => this.toastr.error(this.mensagemErro(erro, 'Não foi possível descartar a venda')),
    });
    this.focarLeitura();
  }

  private mensagemErro(erro: unknown, padrao: string): string {
    const corpo = erro instanceof HttpErrorResponse ? (erro.error as Partial<ErrorModel> | null) : null;
    return corpo?.message ?? padrao;
  }

  /** Cancela o rascunho do servidor (uma vez) e zera a sessão de caixa, inclusive a chave. */
  private encerrarSessao(): void {
    const id = this.zerarSessao();
    if (id !== null) {
      this.pdvService.cancelar(id).subscribe({
        error: (erro: unknown) => this.toastr.error(this.mensagemErro(erro, 'Não foi possível cancelar a venda')),
      });
    }
  }

  /** Zera o estado de sincronização e a chave; devolve o id do rascunho que existia. */
  private zerarSessao(): number | null {
    const id = this.vendaId;
    this.sessao++;
    this.emVoo = false;
    this.sujo = false;
    this.tentativa = 0;
    this.limparRetry();
    this.vendaId = null;
    this.ultimoDescontoAceito = null;
    this.precosAlterados = new Set<number>();
    this.zerarChave();
    this.statusSync = 'sincronizado';
    this.idCliente = null;
    this.clienteAnonimo = true;
    this.clienteNome = null;
    this.clienteDocumento = '';
    this.resumoVenda = null;
    return id;
  }

  // ---------- Saída da tela ----------

  /** Usado pelo canDeactivate: com itens no grid pede confirmação e cancela o rascunho. */
  podeSair(): boolean | Observable<boolean> {
    if (this.vendaConcluida) return true;
    if (this.itens.length === 0 && this.vendaId === null) return true;
    this.confirmarSaidaAberto = true;
    this.saidaResposta$ = new Subject<boolean>();
    this.cdr.detectChanges();
    return this.saidaResposta$;
  }

  get saidaMensagem(): string {
    return this.statusSync === 'sincronizado' ? this.saidaMessage : this.saidaMessageNaoSincronizada;
  }

  confirmarSaida(): void {
    this.confirmarSaidaAberto = false;
    this.encerrarSessao();
    this.saidaResposta$?.next(true);
    this.saidaResposta$?.complete();
    this.saidaResposta$ = null;
  }

  cancelarSaida(): void {
    this.confirmarSaidaAberto = false;
    this.saidaResposta$?.next(false);
    this.saidaResposta$?.complete();
    this.saidaResposta$ = null;
    this.cdr.detectChanges();
    this.focarLeitura();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.itens.length > 0 && this.statusSync !== 'sincronizado') {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  // ---------- Leitura ----------

  onEnterOuTab(event: Event): void {
    event.preventDefault();
    const codigo = normalizarLeitura(this.codigoControl.value);
    this.codigoControl.setValue('');
    if (this.emFechamento) return;
    if (this.rascunhoPendente) {
      if (codigo) this.toastr.info('Retome ou descarte a venda em andamento antes de ler novos itens.');
      return;
    }
    if (!codigo) {
      this.focarLeitura();
      return;
    }
    this.leiturasPendentes++;
    this.finalizarHabilitado = this.calcularFinalizarHabilitado();
    this.leituras$.next({ codigo });
  }

  private resolverProduto(codigo: string): Observable<ResultadoLeitura> {
    const emCache = this.cache.get(codigo);
    if (emCache) {
      return of({ ok: true, produto: emCache });
    }
    return this.pdvService.buscarProdutoPorCodigoBarra(codigo).pipe(
      map((produto): ResultadoLeitura => {
        if (!produto || !(produto.precoUnitario > 0)) {
          return { ok: false, mensagem: `Produto sem preço: ${codigo}` };
        }
        this.cache.set(codigo, produto);
        return { ok: true, produto };
      }),
      catchError((erro: unknown) => of<ResultadoLeitura>({ ok: false, mensagem: this.mensagemErroLeitura(erro, codigo) })),
    );
  }

  private mensagemErroLeitura(erro: unknown, codigo: string): string {
    const corpo = erro instanceof HttpErrorResponse ? (erro.error as Partial<ErrorModel> | null) : null;
    switch (corpo?.code) {
      case PDV_ERROS_LEITURA.PRODUTO_NAO_ENCONTRADO:
        return `Produto não cadastrado: ${codigo}`;
      case PDV_ERROS_LEITURA.PRODUTO_SEM_PRECO:
        return `Produto sem preço: ${codigo}`;
      case PDV_ERROS_LEITURA.PRODUTO_CODIGO_BARRA_DUPLICADO:
        return 'Código duplicado no cadastro — corrija o cadastro';
    }
    if (erro instanceof HttpErrorResponse && erro.status === 404) {
      return `Produto não cadastrado: ${codigo}`;
    }
    return corpo?.message ?? `Falha ao ler o código ${codigo}`;
  }

  private aplicarLeitura(resultado: ResultadoLeitura): void {
    if (this.emFechamento) {
      this.toastr.warning('Leitura descartada: a venda está em fechamento.');
      this.recalcular();
      return;
    }
    if (!resultado.ok) {
      this.erroLeitura = resultado.mensagem;
      this.beep();
    } else {
      this.erroLeitura = null;
      const produto = this.paraCarrinho(resultado.produto);
      this.itens = adicionarOuSomar(this.itens, produto, 1);
      this.selecionadoId = produto.produtoId;
      this.ultimoLidoId = produto.produtoId;
    }
    this.recalcular();
    if (resultado.ok) this.solicitarSync();
    this.focarLeitura();
  }

  private paraCarrinho(p: ProdutoPdv): ProdutoCarrinho {
    return {
      produtoId: p.id,
      codigoBarra: p.codigoBarra,
      descricao: p.nomeProduto || p.descricao,
      unMedida: p.unMedida,
      permiteFracionado: p.permiteFracionado,
      precoUnitario: p.precoUnitario,
    };
  }

  // ---------- Totais ----------

  get descontoPercentual(): number | null {
    return this.descontoControl.value;
  }

  get descontoInvalido(): boolean {
    const pct = this.descontoControl.value;
    return pct !== null && !descontoValido(pct, this.descontoMaximo);
  }

  private recalcular(): void {
    const invalido = this.descontoInvalido;
    const totais = calcularTotais(this.itens, invalido ? 0 : this.descontoControl.value);
    this.totalCentavos = totais.totalCentavos;
    this.subtotalFormatado = formatarMoedaBRL(totais.subtotalCentavos / 100);
    this.descontoValorFormatado = formatarMoedaBRL(totais.descontoCentavos / 100);
    this.totalFormatado = formatarMoedaBRL(totais.totalCentavos / 100);
    this.finalizarHabilitado = this.calcularFinalizarHabilitado(totais.totalCentavos);
    // Último lido no topo.
    this.linhas = [...this.itens].reverse().map((item) => ({
      item,
      totalFormatado: formatarMoedaBRL(totalLinhaCentavos(item) / 100),
      precoFormatado: formatarMoedaBRL(item.precoUnitario),
      quantidadeFormatada: formatarNumeroPtBR(item.quantidade, item.permiteFracionado ? 3 : 0),
    }));
    this.cdr.detectChanges();
  }

  private calcularFinalizarHabilitado(totalCentavos: number = this.totalCentavos): boolean {
    return (
      this.itens.length > 0 &&
      totalCentavos > 0 &&
      !this.descontoInvalido &&
      this.statusSync === 'sincronizado' &&
      this.leiturasPendentes === 0 &&
      !this.rascunhoPendente
    );
  }

  private carregarLimiteDesconto(): void {
    this.parametrosService.getParametrosByCategorias([PDV_DESCONTO_CATEGORIA]).subscribe({
      next: (parametros) => {
        const param = parametros.find((p) => p.categoria === PDV_DESCONTO_CATEGORIA);
        const valor = Number(String(param?.valorMaximo ?? '').trim().replace(',', '.'));
        if (param && String(param.valorMaximo ?? '').trim() !== '' && Number.isFinite(valor) && valor >= 0) {
          this.descontoMaximo = valor;
          this.recalcular();
        }
      },
      error: () => {
        // Mantém o limite padrão; a validação que vale é a do backend.
      },
    });
  }

  // ---------- Remoção / quantidade ----------

  remover(produtoId: number): void {
    const indice = this.itens.findIndex((i) => i.produtoId === produtoId);
    if (indice < 0) return;
    const item = this.itens[indice];
    const exibidos = this.linhas.map((l) => l.item.produtoId);
    const posExibida = exibidos.indexOf(produtoId);
    this.itens = removerItem(this.itens, produtoId);
    this.removidos.push({ item, indice });
    if (this.selecionadoId === produtoId) {
      const proximo = exibidos[posExibida + 1] ?? exibidos[posExibida - 1] ?? null;
      this.selecionadoId = proximo;
    }
    if (this.ultimoLidoId === produtoId) this.ultimoLidoId = null;
    this.cancelarEdicao();
    this.recalcular();
    this.solicitarSync();
    this.focarLeitura();
    const toast = this.toastr.info('Item removido. Clique aqui para desfazer.', item.descricao, {
      timeOut: TEMPO_DESFAZER_MS,
    });
    toast?.onTap?.subscribe(() => this.desfazerRemocao(item.produtoId));
  }

  desfazerRemocao(produtoId: number): void {
    const pos = this.removidos.findIndex((r) => r.item.produtoId === produtoId);
    if (pos < 0) return;
    const [{ item, indice }] = this.removidos.splice(pos, 1);
    if (this.itens.some((i) => i.produtoId === produtoId)) return;
    const copia = [...this.itens];
    copia.splice(Math.min(indice, copia.length), 0, item);
    this.itens = copia;
    this.selecionadoId = produtoId;
    this.recalcular();
    this.solicitarSync();
    this.focarLeitura();
  }

  iniciarEdicaoQuantidade(produtoId?: number): void {
    const id = produtoId ?? this.selecionadoId ?? this.linhas[0]?.item.produtoId ?? null;
    const item = this.itens.find((i) => i.produtoId === id);
    if (!item) return;
    this.editandoId = item.produtoId;
    this.selecionadoId = item.produtoId;
    this.erroQuantidade = null;
    this.quantidadeControl.setValue(String(item.quantidade).replace('.', ','));
    this.cdr.detectChanges();
    setTimeout(() => {
      this.qtdInput?.nativeElement.focus();
      this.qtdInput?.nativeElement.select();
    }, 0);
  }

  confirmarQuantidade(): void {
    if (this.editandoId === null) return;
    const item = this.itens.find((i) => i.produtoId === this.editandoId);
    if (!item) {
      this.cancelarEdicao();
      return;
    }
    const parsed = parseQuantidade(this.quantidadeControl.value, item.permiteFracionado);
    if (!parsed.valida) {
      this.erroQuantidade = parsed.motivo;
      this.cdr.detectChanges();
      return;
    }
    const resultado = alterarQuantidade(this.itens, item.produtoId, parsed.quantidade);
    if (resultado.erro) {
      this.erroQuantidade = resultado.erro;
      this.cdr.detectChanges();
      return;
    }
    this.itens = resultado.itens;
    this.cancelarEdicao();
    this.recalcular();
    this.solicitarSync();
    this.focarLeitura();
  }

  cancelarEdicao(): void {
    this.editandoId = null;
    this.erroQuantidade = null;
    this.quantidadeControl.setValue('');
  }

  selecionar(produtoId: number): void {
    this.selecionadoId = produtoId;
    this.cdr.detectChanges();
  }

  // ---------- Finalizar / cancelar ----------

  /** True enquanto qualquer etapa do fechamento (finalizar, pagamento, cupom) ocupa a tela. */
  get emFechamento(): boolean {
    return this.finalizando || this.vendaModalAberto || this.cupomAberto || this.reabrindo || this.concluindo;
  }

  /** F9: congela o orçamento (uma única chamada) e abre o pagamento no venda-modal-v2. */
  finalizar(): void {
    if (this.emFechamento || !this.finalizarHabilitado) return;
    // Defesa: a atualização de itens pendente precisa estar drenada antes de finalizar.
    if (this.emVoo || this.sujo || this.vendaId === null || this.statusSync !== 'sincronizado') return;
    this.limparRetry();
    this.finalizando = true;
    this.finalizarHabilitado = false;
    this.cdr.detectChanges();
    this.pdvService.finalizar(this.vendaId).subscribe({
      next: (retorno) => {
        this.finalizando = false;
        this.abrirPagamento(retorno);
      },
      error: (erro: unknown) => {
        this.finalizando = false;
        this.finalizarHabilitado = this.calcularFinalizarHabilitado();
        this.toastr.error(this.mensagemErro(erro, 'Não foi possível finalizar a venda'));
        this.cdr.detectChanges();
        this.focarLeitura();
      },
    });
  }

  private abrirPagamento(retorno: PdvFinalizarResponse): void {
    this.resumoVenda = null;
    this.restricaoCliente = retorno.restricaoAtiva === true;
    const hoje = todayLocalDateString();
    this.orcamentoFechamento = {
      id: retorno.id,
      idCliente: retorno.idCliente,
      idVendedor: retorno.idVendedor,
      nomeCliente: retorno.nomeCliente ?? '',
      descontoAplicado: retorno.descontoAplicado,
      valorTotal: retorno.valorTotal,
      dataOrcamento: hoje,
      validadeOrcamento: hoje,
      diasEmAberto: 0,
      restricaoAtiva: this.restricaoCliente,
    };
    this.vendaModalAberto = true;
    this.cdr.detectChanges();
  }

  onVendaEfetuada(resumo: ResumoVendaV2): void {
    this.resumoVenda = resumo;
  }

  onVendaModalClose(concluida: boolean): void {
    if (concluida) {
      this.concluirVenda();
    } else {
      this.reabrirVenda();
    }
  }

  /** Pagamento cancelado: devolve o orçamento a PDV_EM_ANDAMENTO e volta ao grid com os itens. */
  private reabrirVenda(): void {
    if (this.reabrindo || this.vendaId === null) return;
    this.reabrindo = true;
    this.pdvService.reabrir(this.vendaId).subscribe({
      next: (venda) => {
        this.reabrindo = false;
        this.vendaModalAberto = false;
        this.orcamentoFechamento = null;
        this.resumoVenda = null;
        this.reconciliar(venda);
        this.cdr.detectChanges();
        this.focarLeitura();
      },
      error: (erro: unknown) => {
        // Mantém o pagamento aberto: o orçamento segue ABERTO no servidor e o grid fica preservado.
        this.reabrindo = false;
        this.toastr.error(this.mensagemErro(erro, 'Não foi possível reabrir a venda'));
        this.cdr.detectChanges();
      },
    });
  }

  /** Venda concluída: monta o cupom (empresa/vendedor com erro silencioso) e o exibe. */
  private concluirVenda(): void {
    const orcamento = this.orcamentoFechamento;
    this.vendaModalAberto = false;
    this.orcamentoFechamento = null;
    if (!orcamento) {
      this.cdr.detectChanges();
      return;
    }
    const resumo = this.resumoVenda;
    const anonimo = this.clienteAnonimo;
    const nomeCliente = anonimo ? 'Consumidor final' : (orcamento.nomeCliente || this.clienteNome || 'Consumidor final');
    const documento = anonimo ? '' : this.clienteDocumento;
    // A venda já existe no servidor: encerra a sessão sem cancelar e libera a saída da tela.
    this.zerarSessao();
    this.vendaConcluida = true;
    this.concluindo = true;
    forkJoin({
      empresas: this.cadastroService.getEmpresas().pipe(catchError(() => of<Empresa[]>([]))),
      vendedor: this.cadastroService.getVendedorById(orcamento.idVendedor).pipe(catchError(() => of<VendedorCadastro | null>(null))),
    }).subscribe(({ empresas, vendedor }) => {
      const forma = resumo?.formaPagamento ?? '';
      this.cupom = {
        numeroReferencia: orcamento.id,
        emitidoEm: new Date(),
        nomeVendedor: vendedor ? `${vendedor.nomeFuncionario} ${vendedor.sobrenomeFuncionario}`.trim() : '',
        nomeCliente,
        cpfCnpjCliente: documento,
        subtotal: orcamento.valorTotal + orcamento.descontoAplicado,
        desconto: orcamento.descontoAplicado,
        valorTotal: orcamento.valorTotal,
        observacao: '',
        tipoVenda: CUPOM_LABELS_TIPO_VENDA[resumo?.tipoVendaPagamento ?? ''] ?? (resumo?.tipoVendaPagamento ?? ''),
        formaPagamento: CUPOM_LABELS_FORMA[forma] ?? forma,
        bandeira: resumo?.bandeiraCartao ?? '',
        numParcelas: resumo?.numParcelas ?? 1,
        valorEntrada: resumo?.valorEntrada ?? null,
        previsaoEntrega: resumo?.previsaoEntrega ?? '',
        empresa: (empresas ?? []).find((e) => e.status === true) ?? null,
      };
      this.cupomAberto = true;
      this.concluindo = false;
      this.cdr.detectChanges();
    });
  }

  /** Sair do cupom: venda em branco, nova chave e campo de leitura focado. */
  fecharCupom(): void {
    this.cupomAberto = false;
    this.cupom = null;
    this.vendaConcluida = false;
    this.resumoVenda = null;
    this.zerarSessao();
    this.limparVendaLocal();
    this.focarLeitura();
  }

  private limparVendaLocal(): void {
    this.itens = [];
    this.selecionadoId = null;
    this.ultimoLidoId = null;
    this.removidos = [];
    this.erroLeitura = null;
    this.descontoControl.setValue(null, { emitEvent: false });
    this.cancelarEdicao();
    this.recalcular();
  }

  // ---------- Identificação de cliente (P7) ----------

  /** F8: abre a busca de cliente; a seleção chega pelo ModalStateService (take(1)). */
  identificarCliente(): void {
    if (this.emFechamento || this.rascunhoPendente || this.clienteModalAberto) return;
    this.identificacaoSub?.unsubscribe();
    this.identificacaoSub = this.modalStateService.modalResult$
      .pipe(filter((c): c is ClienteSimplificado => !!c && c.id > 0), take(1))
      .subscribe((cliente) => this.aplicarCliente(cliente));
    this.clienteModalAberto = true;
    this.cdr.detectChanges();
  }

  fecharClienteModal(): void {
    this.clienteModalAberto = false;
    this.identificacaoSub?.unsubscribe();
    this.identificacaoSub = null;
    this.cdr.detectChanges();
    this.focarLeitura();
  }

  private aplicarCliente(cliente: ClienteSimplificado): void {
    const anterior = this.vendaId;
    // O cliente só é definido na criação: recria o rascunho (nova chave) com os itens atuais.
    if (anterior !== null) this.zerarSessao();
    this.idCliente = cliente.id;
    this.clienteNome = cliente.nomeRazaoSocial;
    this.clienteDocumento = cliente.cpfCnpj ?? '';
    this.clienteAnonimo = false;
    if (anterior !== null) {
      this.pdvService.cancelar(anterior).subscribe({ error: () => undefined });
      this.solicitarSync();
    }
    this.cdr.detectChanges();
  }

  pedirCancelamento(): void {
    if (this.itens.length === 0) {
      this.focarLeitura();
      return;
    }
    this.confirmarCancelamentoAberto = true;
    this.cdr.detectChanges();
  }

  confirmarCancelamento(): void {
    this.encerrarSessao();
    this.confirmarCancelamentoAberto = false;
    this.limparVendaLocal();
    this.focarLeitura();
  }

  fecharCancelamento(): void {
    this.confirmarCancelamentoAberto = false;
    this.cdr.detectChanges();
    this.focarLeitura();
  }

  // ---------- Atalhos ----------

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.emFechamento || this.clienteModalAberto) return;
    if (this.confirmarSaidaAberto) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.cancelarSaida();
      }
      return;
    }
    if (this.confirmarCancelamentoAberto) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.fecharCancelamento();
      }
      return;
    }
    const alvo = event.target as HTMLElement | null;
    const emCampo = alvo instanceof HTMLInputElement || alvo instanceof HTMLTextAreaElement;
    switch (event.key) {
      case 'F2':
        event.preventDefault();
        this.iniciarEdicaoQuantidade();
        break;
      case 'F4':
        event.preventDefault();
        this.focarDesconto();
        break;
      case 'F6':
        event.preventDefault();
        this.removerSelecionado();
        break;
      case 'Delete':
        // Em campo com texto, Delete apaga caractere; só remove com o campo de leitura vazio.
        if (!emCampo || (alvo === this.leituraInput?.nativeElement && !this.codigoControl.value)) {
          event.preventDefault();
          this.removerSelecionado();
        }
        break;
      case 'F9':
        event.preventDefault();
        this.finalizar();
        break;
      case 'F8':
        event.preventDefault();
        this.identificarCliente();
        break;
      case 'Escape':
        event.preventDefault();
        if (this.editandoId !== null) {
          this.cancelarEdicao();
          this.focarLeitura();
        } else {
          this.pedirCancelamento();
        }
        break;
      case 'ArrowDown':
        if (!emCampo || alvo === this.leituraInput?.nativeElement) {
          event.preventDefault();
          this.moverSelecao(1);
        }
        break;
      case 'ArrowUp':
        if (!emCampo || alvo === this.leituraInput?.nativeElement) {
          event.preventDefault();
          this.moverSelecao(-1);
        }
        break;
    }
  }

  private removerSelecionado(): void {
    if (this.selecionadoId !== null) this.remover(this.selecionadoId);
  }

  private moverSelecao(delta: number): void {
    if (this.linhas.length === 0) return;
    const ids = this.linhas.map((l) => l.item.produtoId);
    const atual = this.selecionadoId === null ? -1 : ids.indexOf(this.selecionadoId);
    const proximo = Math.min(ids.length - 1, Math.max(0, (atual < 0 ? (delta > 0 ? -1 : ids.length) : atual) + delta));
    this.selecionadoId = ids[proximo];
    this.cdr.detectChanges();
  }

  // ---------- Foco ----------

  focarDesconto(): void {
    const el = document.querySelector<HTMLInputElement>('[data-testid="pdv-desconto-input"]');
    el?.focus();
    el?.select();
  }

  focarLeitura(): void {
    setTimeout(() => this.leituraInput?.nativeElement.focus(), 0);
  }

  /** Recupera o foco quando nada legítimo o tem (o leitor "digita" no elemento focado). */
  onLeituraBlur(): void {
    setTimeout(() => {
      const ativo = document.activeElement;
      const semFoco = !ativo || ativo === document.body;
      if (semFoco && !this.confirmarCancelamentoAberto) {
        this.leituraInput?.nativeElement.focus();
      }
    }, 0);
  }

  onDescontoBlur(): void {
    this.focarLeitura();
  }

  private beep(): void {
    try {
      const Ctx = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      osc.frequency.value = 220;
      osc.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
      osc.onended = () => void ctx.close();
    } catch {
      // Som é opcional.
    }
  }
}
